import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CalendarEvent, CalendarInfo } from 'zitimail-shared-types';

import { calendar } from '../api/calendar';
import {
  addDays,
  dayKey,
  eventDays,
  startOfDay,
  step,
  timeLabel,
  title,
  viewRange,
  type CalendarMode,
} from './dates';
import { EventEditor, type Draft } from './EventEditor';
import { MiniMonth } from './MiniMonth';
import { startsAt } from './reminders';
import { QuickCreate } from './QuickCreate';
import { WeekGrid } from './WeekGrid';
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '../components/icons';

const MODES: CalendarMode[] = ['day', 'week', 'month', 'agenda'];

/** `show`: an event to open, as a reminder does; on its day. */
export function CalendarView({
  email,
  show,
}: {
  email: string;
  show?: CalendarEvent | undefined;
}): JSX.Element {
  // A week is seven slivers on a phone: a day reads.
  const [mode, setMode] = useState<CalendarMode>(() =>
    typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 760px)').matches
      ? 'day'
      : 'week',
  );
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [calendars, setCalendars] = useState<CalendarInfo[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [quick, setQuick] = useState<(Draft & { x: number; y: number }) | null>(null);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => viewRange(mode, anchor), [mode, anchor]);
  const colour = useMemo(() => new Map(calendars.map((c) => [c.href, c.color])), [calendars]);

  const load = useCallback(() => {
    calendar
      .events(range.start, range.end)
      .then((list) => {
        setEvents(list);
        setError(null);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not load the calendar.'),
      );
  }, [range]);

  const loadCalendars = useCallback(() => {
    calendar
      .calendars()
      .then(setCalendars)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not load the calendar.'),
      );
  }, []);
  useEffect(loadCalendars, [loadCalendars]);
  useEffect(load, [load]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      if (hidden.has(event.calendar)) {
        continue;
      }
      for (const key of eventDays(event)) {
        map.set(key, [...(map.get(key) ?? []), event]);
      }
    }
    return map;
  }, [events, hidden]);

  const newAt = (start: Date, allDay = false) =>
    setDraft({
      start,
      end: allDay ? addDays(start, 1) : new Date(start.getTime() + 3_600_000),
      allDay,
    });
  /**
   * Saves a moved or resized event: the same event, new times. Shown in place
   * at once; reloaded from the server either way, so a refusal (someone
   * else's event, an edit made elsewhere) puts it back.
   */
  const move = (event: CalendarEvent, start: Date, end: Date) => {
    if (event.organizer && event.organizer !== email.toLowerCase()) {
      setError(`Only the organizer (${event.organizer}) can move this event.`);
      return;
    }
    const iso = (d: Date) => (event.allDay ? `${dayKey(d)}T00:00:00.000Z` : d.toISOString());
    setEvents((list) =>
      list.map((x) =>
        x.href === event.href && x.start === event.start
          ? { ...x, start: iso(start), end: iso(end) }
          : x,
      ),
    );
    calendar
      .update(event.href, event.etag, {
        calendar: event.calendar,
        title: event.title,
        start: iso(start),
        end: iso(end),
        allDay: event.allDay,
        ...(event.location ? { location: event.location } : {}),
        ...(event.description ? { description: event.description } : {}),
        repeat: 'none',
        attendees: event.attendees.map((a) => a.email),
        ...(event.reminderMinutes !== undefined ? { reminderMinutes: event.reminderMinutes } : {}),
      })
      .then(() => setError(null))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Not moved.'))
      .finally(load);
  };

  const open = (event: CalendarEvent) =>
    setDraft({
      event,
      start: new Date(event.start),
      end: new Date(event.end),
      allDay: event.allDay,
    });

  useEffect(() => {
    if (show) {
      setAnchor(startOfDay(new Date(startsAt(show))));
      open(show);
    }
    // `open` is made anew each render; the event is what changes.
  }, [show]);

  const chip = (event: CalendarEvent) => (
    <button
      type="button"
      key={`${event.href}:${event.start}`}
      className={`chip${event.attendees.some((a) => a.email === email.toLowerCase() && a.partstat === 'DECLINED') ? ' declined' : ''}`}
      style={{ borderLeftColor: colour.get(event.calendar) ?? 'var(--accent)' }}
      onClick={(e) => {
        e.stopPropagation();
        open(event);
      }}
      title={event.recurring ? `${event.title} — repeating; open it to change` : event.title}
      draggable={!event.recurring}
      onDragStart={(e) => {
        dragged.current = event;
        e.dataTransfer.effectAllowed = 'move';
      }}
    >
      {!event.allDay && <span className="chip-time">{timeLabel(event.start)}</span>} {event.title}
    </button>
  );

  // Month view: an event dropped on another day keeps its time and length.
  const dragged = useRef<CalendarEvent | null>(null);
  const dropOn = (day: Date) => {
    const event = dragged.current;
    dragged.current = null;
    if (!event) {
      return;
    }
    const start = new Date(event.start);
    const from = event.allDay ? new Date(event.start.slice(0, 10) + 'T00:00:00') : start;
    const shift = Math.round((startOfDay(day).getTime() - startOfDay(from).getTime()) / 86_400_000);
    if (shift !== 0) {
      move(event, addDays(start, shift), addDays(new Date(event.end), shift));
    }
  };

  const days = Math.round((range.end.getTime() - range.start.getTime()) / 86_400_000);
  const today = dayKey(new Date());

  const busyDays = useMemo(() => new Set(byDay.keys()), [byDay]);

  return (
    <main className="main calendar-layout">
      <aside className="cal-side" aria-label="Calendars">
        <button
          type="button"
          className="compose"
          onClick={() => newAt(new Date(Math.ceil(Date.now() / 1_800_000) * 1_800_000))}
        >
          <PlusIcon /> New event
        </button>
        <MiniMonth
          key={anchor.getFullYear() * 12 + anchor.getMonth()}
          selected={anchor}
          shown={range}
          busy={busyDays}
          onPick={(d) => setAnchor(startOfDay(d))}
        />
        <div className="cal-list">
          <h4>My calendars</h4>
          {calendars.map((c) => (
            <div key={c.href} className="cal-item">
              <input
                type="checkbox"
                checked={!hidden.has(c.href)}
                aria-label={`Show ${c.name}`}
                style={{ accentColor: c.color }}
                onChange={() =>
                  setHidden((h) => {
                    const next = new Set(h);
                    if (next.has(c.href)) {
                      next.delete(c.href);
                    } else {
                      next.add(c.href);
                    }
                    return next;
                  })
                }
              />
              <span className="cal-name">{c.name}</span>
              <label
                className="swatch"
                style={{ background: c.color }}
                title={`Colour of ${c.name}`}
              >
                <input
                  type="color"
                  value={c.color}
                  aria-label={`Colour of ${c.name}`}
                  onChange={(e) => {
                    const color = e.target.value;
                    setCalendars((list) =>
                      list.map((x) => (x.href === c.href ? { ...x, color } : x)),
                    );
                  }}
                  onBlur={(e) =>
                    void calendar
                      .setAppearance(c.href, { color: e.target.value })
                      .catch((err: unknown) =>
                        setError(err instanceof Error ? err.message : 'Not saved.'),
                      )
                  }
                />
              </label>
            </div>
          ))}
          <button
            type="button"
            className="link-btn"
            onClick={() => {
              const name = window.prompt('Name of the new calendar');
              if (name?.trim()) {
                const palette = ['#5B6CFF', '#D9480F', '#2B8A3E', '#AE3EC9', '#E8590C', '#1971C2'];
                calendar
                  .createCalendar(
                    name.trim(),
                    palette[calendars.length % palette.length] ?? '#5B6CFF',
                  )
                  .then(loadCalendars)
                  .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Not created.'));
              }
            }}
          >
            Add a calendar
          </button>
        </div>
      </aside>
      <section className="calendar" aria-label="Calendar">
        <div className="cal-toolbar">
          <button
            type="button"
            className="btn ghost"
            style={{ width: 'auto' }}
            onClick={() => setAnchor(startOfDay(new Date()))}
          >
            Today
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label="Previous"
            onClick={() => setAnchor(step(mode, anchor, -1))}
          >
            <ChevronLeftIcon />
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label="Next"
            onClick={() => setAnchor(step(mode, anchor, 1))}
          >
            <ChevronRightIcon />
          </button>
          <h2>{title(mode, anchor)}</h2>
          <span className="seg" role="tablist" aria-label="View">
            {MODES.map((m) => (
              <button
                type="button"
                role="tab"
                key={m}
                aria-selected={mode === m}
                className={mode === m ? 'on' : ''}
                onClick={() => setMode(m)}
              >
                {m[0]?.toUpperCase() + m.slice(1)}
              </button>
            ))}
          </span>
          {/* The side panel, with its New event, is hidden on narrow screens. */}
          <button
            type="button"
            className="btn cal-new"
            onClick={() => newAt(new Date(Math.ceil(Date.now() / 1_800_000) * 1_800_000))}
          >
            <PlusIcon /> New event
          </button>
        </div>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}

        {mode === 'month' && (
          <div className="month">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="month-head">
                {d}
              </div>
            ))}
            {Array.from({ length: days }, (_, i) => addDays(range.start, i)).map((d) => {
              const key = dayKey(d);
              const list = byDay.get(key) ?? [];
              return (
                <div
                  key={key}
                  className={`month-cell${d.getMonth() !== anchor.getMonth() ? ' other' : ''}${key === today ? ' today' : ''}`}
                  onDragOver={(e) => {
                    if (dragged.current) {
                      e.preventDefault();
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    dropOn(d);
                  }}
                  onClick={(e) =>
                    setQuick({
                      start: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
                      end: new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1),
                      allDay: true,
                      x: e.clientX,
                      y: e.clientY,
                    })
                  }
                >
                  <span className="month-day">{d.getDate()}</span>
                  {list.slice(0, 3).map(chip)}
                  {list.length > 3 && (
                    <button
                      type="button"
                      className="more"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchor(d);
                        setMode('day');
                      }}
                    >
                      +{list.length - 3} more
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {(mode === 'week' || mode === 'day') && (
          <WeekGrid
            days={Array.from({ length: days }, (_, i) => addDays(range.start, i))}
            eventsOn={(key) => byDay.get(key) ?? []}
            colour={(e) => colour.get(e.calendar) ?? 'var(--accent)'}
            today={today}
            onCreate={(start, end, point) => setQuick({ start, end, allDay: false, ...point })}
            onOpen={open}
            onMove={move}
          />
        )}

        {mode === 'agenda' && (
          <div className="agenda">
            {events.length === 0 && <p className="muted">Nothing in the next 30 days.</p>}
            {Array.from({ length: days }, (_, i) => addDays(range.start, i))
              .filter((d) => byDay.has(dayKey(d)))
              .map((d) => (
                <section key={dayKey(d)}>
                  <h3>
                    {d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' })}
                  </h3>
                  {(byDay.get(dayKey(d)) ?? []).map((e) => (
                    <button
                      type="button"
                      key={`${e.href}:${e.start}`}
                      className="agenda-row"
                      onClick={() => open(e)}
                    >
                      <span className="mail-date">
                        {e.allDay ? 'All day' : `${timeLabel(e.start)} – ${timeLabel(e.end)}`}
                      </span>
                      <span
                        className="dot"
                        style={{ background: colour.get(e.calendar) ?? 'var(--accent)' }}
                      />
                      <span>
                        {e.title}
                        {e.location && <span className="muted"> · {e.location}</span>}
                      </span>
                    </button>
                  ))}
                </section>
              ))}
          </div>
        )}
      </section>

      {quick && (
        <QuickCreate
          {...quick}
          calendars={calendars}
          onClose={() => setQuick(null)}
          onSaved={() => {
            setQuick(null);
            load();
          }}
          onMore={(title) => {
            setQuick(null);
            setDraft({
              start: quick.start,
              end: quick.end,
              allDay: quick.allDay,
              ...(title ? { title } : {}),
            });
          }}
        />
      )}
      {draft && (
        <EventEditor
          draft={draft}
          calendars={calendars}
          me={email}
          onClose={() => setDraft(null)}
          onSaved={() => {
            setDraft(null);
            load();
          }}
        />
      )}
    </main>
  );
}
